import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import {
  Bar,
  ComposedChart,
  Customized,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  LabelList,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BAR_MARGIN_PER_MARKET,
  BAR_TOP_RADIUS,
  CHART_OPACITY,
  monthKeyFromStartDate,
  gbToTb,
  calendarPrevYm,
  TICK_FONT_SIZE,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarGrid,
  dashboardBarSizingProps,
  DashboardBarPlotHoverCursor,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { DashboardSummarySection, SummaryCard } from '@/domains/global-dashboard/sections/DashboardSummarySection';
import { SectionType } from '@/domains/global-dashboard/utils/chart-utils';
import { DataConsumptionType, getDataConsumptionTypeTextColorClass, getDataConsumptionTypeChartFill, getUtilizationTextColorClass } from '@/shared/utils/color-utils-data-usages';
import { cn } from '@/shared/utils/date-utils';
import { datapoolSnapshotByYm } from '@/domains/global-dashboard/sections/consumption/data-consumption-utils';
import { DataConsumptionEvolutionChart } from '@/domains/global-dashboard/sections/consumption/DataConsumptionEvolutionChart';

/** Capped ↔ uncapped forecast bracket (aligned with sketch). */
const DATAPOOL_FORECAST_BRACKET = 'red';

/** One row fed to Recharts bars + overlays (TB). */
type DatapoolMarketChartRow = {
  row: OrganizationOverviewData;
  consumed: number;
  unused: number;
  contractedTb: number;
  cappedTb: number;
  uncappedTb: number;
};

type BarLayoutRect = { x?: number; y?: number; width?: number; height?: number; payload?: DatapoolMarketChartRow };

/** Recharts puts `data` on `props`; `dataKey` lives on the original `item` element. */
type FormattedBarItem = {
  item?: { props?: { dataKey?: string | number } };
  props?: { data?: BarLayoutRect[] };
};

/** Get the bar rectangles for a given data key. */
function barRectsForDataKey(items: FormattedBarItem[] | undefined, dataKey: string): BarLayoutRect[] | undefined {
  if (!items?.length) return undefined;
  const byKey = items.find((g) => g.item?.props?.dataKey === dataKey)?.props?.data;
  if (byKey?.length) return byKey;
  const firstBar = items.find((g) => Array.isArray(g.props?.data) && (g.props?.data?.length ?? 0) > 0);
  return firstBar?.props?.data;
}

/** Overlays for the datapool market chart. */
function DatapoolPerMarketOverlays(chartProps: Record<string, unknown>) {
  const formattedGraphicalItems = chartProps.formattedGraphicalItems as FormattedBarItem[] | undefined;
  const yAxisMap = chartProps.yAxisMap as Record<string, { scale: (v: number) => number }> | undefined;

  const yAxis = yAxisMap ? yAxisMap[Object.keys(yAxisMap)[0] ?? ''] : undefined;
  const yScale = yAxis?.scale;
  const consumedRects = barRectsForDataKey(formattedGraphicalItems, 'consumed');

  if (!consumedRects?.length || typeof yScale !== 'function') {
    return null;
  }

  return (
    <g className="recharts-layer datapool-market-overlays" pointerEvents="none">
      {consumedRects.map((rect, i) => {
        const payload = rect.payload;
        const x = Number(rect.x);
        const width = Number(rect.width);
        if (!payload || !Number.isFinite(x) || !Number.isFinite(width)) return null;

        const contractedY = yScale(payload.contractedTb);
        const cappedY = yScale(payload.cappedTb);
        const uncappedY = yScale(payload.uncappedTb);
        const vx = x + width + 6;
        const tick = 10;
        const labelX = vx + tick + 8;
        const showBracket =
          payload.uncappedTb > 0 ||
          payload.cappedTb > 0;
        // Math.abs(payload.uncappedTb - payload.cappedTb) > 1e-6;
        const yTop = Math.min(cappedY, uncappedY);
        const yBottom = Math.max(cappedY, uncappedY);

        return (
          <g key={`${payload.row.organizationId}-${i}`}>
            <line
              x1={x}
              x2={x + width}
              y1={contractedY}
              y2={contractedY}
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={x + width / 2}
              y={contractedY - 6}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill="hsl(var(--foreground))"
            >
              {formatNumber(payload.contractedTb)}
            </text>
            {showBracket && yBottom > yTop ? (
              <g stroke={DATAPOOL_FORECAST_BRACKET} fill={DATAPOOL_FORECAST_BRACKET}>
                <line
                  x1={vx}
                  x2={vx}
                  y1={yTop}
                  y2={yBottom}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1={vx}
                  x2={vx + tick}
                  y1={cappedY}
                  y2={cappedY}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1={vx}
                  x2={vx + tick}
                  y1={uncappedY}
                  y2={uncappedY}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text x={labelX} y={cappedY + 4} fontSize={11} fontWeight={300} fill={DATAPOOL_FORECAST_BRACKET}>
                  {formatNumber(payload.cappedTb)}
                </text>
                <text x={labelX} y={uncappedY + 4} fontSize={11} fontWeight={300} fill={DATAPOOL_FORECAST_BRACKET}>
                  {formatNumber(payload.uncappedTb)}
                </text>
              </g>
            ) : (yBottom - yTop === 0) ? (
              <g stroke={DATAPOOL_FORECAST_BRACKET} fill={DATAPOOL_FORECAST_BRACKET}>
                <line
                  x1={vx}
                  x2={vx + tick}
                  y1={cappedY}
                  y2={cappedY}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text x={labelX} y={cappedY + 4} fontSize={11} fontWeight={300} fill={DATAPOOL_FORECAST_BRACKET}>
                  {formatNumber(payload.cappedTb)}
                </text>
              </g>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

/** Data consumption section component. */
export function DataConsumptionSection({
  selectedOrganizations,
  accountChartColorById,
  onNavigateToOrganization,
}: {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById: Map<string, string>;
  onNavigateToOrganization: (organizationId: string, sectionType: SectionType) => void;
}) {
  const [poolSurface, setPoolSurface] = useState<'chart' | 'table'>('chart');

  const organizationSeries = useMemo(() => {
    return selectedOrganizations.map((organization) => ({
      organization,
      months: datapoolSnapshotByYm(organization),
    }));
  }, [selectedOrganizations]);

  /** Single pass over responses; all views read from this. */
  const breakdownRows = useMemo(() => {
    const currentMonth = monthKeyFromStartDate(new Date().toISOString());

    type BreakdownRow = {
      organization: OrganizationOverviewData;
      consumedTb: number;
      unusedTb: number;
      contractedToUncappedTb: number;
      utilizationPct: number;
      contractedTb: number;
      cappedTb: number;
      uncappedTb: number;
      netChange: number;
    };

    const breakdownRows: BreakdownRow[] = [];
    for (const { organization, months } of organizationSeries) {
      const snap = months.get(currentMonth);
      if (!snap || snap.contractedGb <= 0) continue;

      const contractedTb = gbToTb(snap.contractedGb);
      const consumedTb = gbToTb(snap.consumedGb);
      const unusedTb = gbToTb(Math.max(0, snap.contractedGb - snap.consumedGb));
      const cappedTb = gbToTb(snap.cappedForecastGb);
      const uncappedTb = gbToTb(snap.uncappedForecastGb);
      const contractedToUncappedTb = gbToTb(Math.max(0, snap.uncappedForecastGb - snap.contractedGb));
      const utilizationPct = contractedTb > 0 ? (consumedTb / contractedTb) * 100 : 0;

      const prevYm = calendarPrevYm(currentMonth);
      const prevSnap = months.get(prevYm);
      if (!prevSnap) continue;

      const consumedTbLastMonth = gbToTb(prevSnap.consumedGb);
      const netChange = consumedTb - consumedTbLastMonth;

      breakdownRows.push({
        organization,
        consumedTb,
        unusedTb,
        contractedToUncappedTb,
        utilizationPct,
        contractedTb,
        cappedTb,
        uncappedTb,
        netChange,
      });
    }
    breakdownRows.sort((a, b) => b.utilizationPct - a.utilizationPct);

    return breakdownRows;
  }, [organizationSeries]);

  const datapoolMarketChartYMax = useMemo(() => {
    let m = 0;
    for (const r of breakdownRows) {
      m = Math.max(m, r.contractedTb, r.consumedTb + r.unusedTb, r.cappedTb, r.uncappedTb);
    }
    return formatNumber(m <= 0 ? 1 : m * 1.1);
  }, [breakdownRows]);

  const barChartRows = useMemo(() => {
    return breakdownRows.map((r) => ({
      row: r.organization,
      consumed: r.consumedTb,
      unused: r.unusedTb,
      contractedTb: r.contractedTb,
      cappedTb: r.cappedTb,
      uncappedTb: r.uncappedTb,
      utilizationPct: r.utilizationPct,
    }));
  }, [breakdownRows]);

  const summaryCards: SummaryCard[] = useMemo(() => {
    const totalContractedTb = breakdownRows.reduce((s, r) => s + r.contractedTb, 0);
    const totalConsumedTb = breakdownRows.reduce((s, r) => s + r.consumedTb, 0);
    const totalCappedTb = breakdownRows.reduce((s, r) => s + r.cappedTb, 0);
    const totalUncappedTb = breakdownRows.reduce((s, r) => s + r.uncappedTb, 0);
    const portfolioUtilization =
      totalContractedTb > 0 ? (totalConsumedTb / totalContractedTb) * 100 : 0;

    return [
      { title: 'Total Contracted', currentValue: `${formatNumber(totalContractedTb)} TB`, currentValueTextColor: getDataConsumptionTypeTextColorClass(DataConsumptionType.CONTRACTED) },
      { title: 'Total Consumed', currentValue: `${formatNumber(totalConsumedTb)} TB`, currentValueTextColor: getDataConsumptionTypeTextColorClass(DataConsumptionType.CONSUMED) },
      { title: 'Utilization', currentValue: `${formatNumber(portfolioUtilization)}%`, currentValueTextColor: getUtilizationTextColorClass(portfolioUtilization) },
      { title: 'Capped Forecast', currentValue: `${formatNumber(totalCappedTb)} TB`, currentValueTextColor: getDataConsumptionTypeTextColorClass(DataConsumptionType.CAPPED_FORECAST), subtitle: '* Linear projection based on average daily consumption, capped by plan limit.' },
      { title: 'Uncapped Forecast', currentValue: `${formatNumber(totalUncappedTb)} TB`, currentValueTextColor: getDataConsumptionTypeTextColorClass(DataConsumptionType.UNCAPPED_FORECAST), subtitle: '* Raw projection without plan constraints.' },
    ];
  }, [breakdownRows]);

  return (
    <section className="space-y-4 border rounded-md p-4 bg-muted/50">
      <div className="flex items-start gap-2 justify-between">
        <div>
          <h2 className="text-lg font-semibold">Data Consumption Overview</h2>
          <p className="text-sm">Executive snapshot and data consumption breakdown</p>
        </div>
        <div className="flex-col text-sm flex items-end gap-2">
          <span className="font-medium">Utilization tiers:</span>
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <span className={cn(getUtilizationTextColorClass(89), 'font-bold')}>&lt;90%</span>
            <span>•</span>
            <span className={cn(getUtilizationTextColorClass(90), 'font-bold')}>&gt;=90% and &lt;100%</span>
            <span>•</span>
            <span className={cn(getUtilizationTextColorClass(100), 'font-bold')}>&gt;=100%</span>
          </div>
        </div>
      </div>

      {/* Data consumption summary (current month) */}
      <DashboardSummarySection
        section={{
          type: SectionType.DATA_CONSUMPTION,
          title: 'Data consumption summary (current month)',
          description: 'Aggregated month-to-date consumption against contracted pool.'
        }}
        summaryCards={summaryCards}
      />

      {/* Data consumption breakdown (current month) */}
      <Card>
        <CardHeader className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Data consumption breakdown (current month)</CardTitle>
              <CardDescription>Consumed and remaining pool, contracted ceiling, and forecast brackets (capped &rarr; uncapped).</CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <ToggleGroup
                type="single"
                value={poolSurface}
                onValueChange={(v) => v && setPoolSurface(v as 'chart' | 'table')}
                size="sm"
                variant="outline"
                className="lg:shrink-0"
              >
                <ToggleGroupItem value="chart">Chart</ToggleGroupItem>
                <ToggleGroupItem value="table">Table</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 overflow-x-auto">
          {poolSurface === 'table' ? (
            <div className="overflow-x-auto rounded-lg border">
              <Table className="w-full border-collapse">
                <TableHeader>
                  <TableRow className="bg-muted font-medium">
                    <TableHead>Market</TableHead>
                    <TableHead className="text-right">Contracted Data</TableHead>
                    <TableHead className="text-right">Consumed Data</TableHead>
                    <TableHead className="text-right">Capped Forecast</TableHead>
                    <TableHead className="text-right">Uncapped Forecast</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdownRows.map((r) => {
                    const utilizationPct = r.utilizationPct;
                    const cappedUtilizationPct = r.cappedTb > 0 ? (r.cappedTb / r.contractedTb) * 100 : 0;
                    const uncappedUtilizationPct = r.uncappedTb > 0 ? (r.uncappedTb / r.contractedTb) * 100 : 0;
                    return (
                      <TableRow
                        key={r.organization.organizationId}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onNavigateToOrganization(r.organization.organizationId, SectionType.DATA_CONSUMPTION)}
                      >
                        <TableCell className="font-medium">{r.organization.name}</TableCell>
                        <TableCell className="text-right">{`${formatNumber(r.contractedTb)} TB`}</TableCell>
                        <TableCell className="text-right">{`${formatNumber(r.consumedTb)} TB`} <span className={`${getUtilizationTextColorClass(utilizationPct)}`}>({formatNumber(utilizationPct)}%)</span></TableCell>
                        <TableCell className="text-right">{`${formatNumber(r.cappedTb)} TB`} <span className={`${getUtilizationTextColorClass(cappedUtilizationPct)}`}>({formatNumber(cappedUtilizationPct)}%)</span></TableCell>
                        <TableCell className="text-right">{`${formatNumber(r.uncappedTb)} TB`} <span className={`${getUtilizationTextColorClass(uncappedUtilizationPct)}`}>({formatNumber(uncappedUtilizationPct)}%)</span></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : barChartRows.length === 0 ? (
            <p className="text-sm py-8 text-center">
              No data consumption rows with contracted capacity for the current month.
            </p>
          ) : (
            <div className="h-[500px] w-full min-w-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={barChartRows}
                  {...dashboardBarSizingProps}
                  margin={{ ...BAR_MARGIN_PER_MARKET, right: 118 }}
                >
                  <DashboardBarGrid />
                  <XAxis dataKey="row.name" tick={{ fontSize: TICK_FONT_SIZE }} interval={0} textAnchor="middle" />
                  <YAxis
                    tick={{ fontSize: TICK_FONT_SIZE }}
                    scale="linear"
                    tickFormatter={(value) => `${formatNumber(value)}`}
                    domain={[0, datapoolMarketChartYMax]}
                  />
                  <RechartsTooltip
                    cursor={<DashboardBarPlotHoverCursor />}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0]?.payload as DatapoolMarketChartRow | undefined;
                      if (!p) return null;
                      return (
                        <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md min-w-[200px]">
                          <p className="font-medium mb-2">{p.row.name}</p>
                          <div className="space-y-1 tabular-nums">
                            <p className="flex justify-between gap-6">
                              <span style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.CONSUMED) }}>Consumed</span>
                              <span>{formatNumber(p.consumed)}</span>
                            </p>
                            <p className="flex justify-between gap-6">
                              <span style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.UNUSED) }}>Unused (within contract)</span>
                              <span>{formatNumber(p.unused)}</span>
                            </p>
                            <p className="flex justify-between gap-6 border-t pt-1 mt-1">
                              <span>Contracted</span>
                              <span>{formatNumber(p.contractedTb)}</span>
                            </p>
                            <p className="flex justify-between gap-6">
                              <span style={{ color: DATAPOOL_FORECAST_BRACKET }}>Capped forecast</span>
                              <span>{formatNumber(p.cappedTb)}</span>
                            </p>
                            <p className="flex justify-between gap-6">
                              <span style={{ color: DATAPOOL_FORECAST_BRACKET }}>Uncapped forecast</span>
                              <span>{formatNumber(p.uncappedTb)}</span>
                            </p>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    content={() => (
                      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs pt-2">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-3 w-3 shrink-0 rounded-sm"
                            style={{ background: getDataConsumptionTypeChartFill(DataConsumptionType.CONSUMED) }}
                          />
                          Consumed
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-3 w-3 shrink-0 rounded-sm"
                            style={{ background: getDataConsumptionTypeChartFill(DataConsumptionType.UNUSED) }}
                          />
                          Unused (within contract)
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-0.5 w-5 shrink-0" style={{ background: 'hsl(var(--foreground))' }} />
                          Contracted
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-3 w-0.5 shrink-0" style={{ background: DATAPOOL_FORECAST_BRACKET }} />
                          Capped / uncapped forecast
                        </span>
                      </div>
                    )}
                  />
                  <Bar dataKey="consumed" stackId="pool" name="Consumed" fill={getDataConsumptionTypeChartFill(DataConsumptionType.CONSUMED)} fillOpacity={CHART_OPACITY.datapoolStack}>
                    <LabelList
                      dataKey="consumed"
                      position="center"
                      fill="hsl(var(--foreground))"
                      fontSize={11}
                      fontWeight={600}
                      formatter={(v: unknown) => {
                        const n = typeof v === 'number' ? v : Number(v);
                        return n <= 0 ? '' : `${formatNumber(n)}`;
                      }}
                    />
                  </Bar>
                  <Bar
                    dataKey="unused"
                    stackId="pool"
                    name="Unused (within contract)"
                    fill={getDataConsumptionTypeChartFill(DataConsumptionType.UNUSED)}
                    fillOpacity={CHART_OPACITY.datapoolStack}
                    radius={BAR_TOP_RADIUS}
                  />
                  <Customized component={DatapoolPerMarketOverlays} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <DataConsumptionEvolutionChart
        selectedOrganizations={selectedOrganizations}
        accountChartColorById={accountChartColorById}
      />
    </section>
  );
}